import type { HotSeatState } from "./useHotSeatMatch";

/**
 * Two-player scoreboard. Highlights whose turn it is, shows each player's
 * stat (score, HP, rounds - depends on game). Used by both games for
 * visual consistency.
 *
 * - `statLabel` describes what the number means ("Score", "HP", "Rounds").
 * - `statMax` is optional; when present the stat renders as a progress bar
 *   (great for HP). When absent it's plain numeric.
 * - `aria-live="polite"` on stat numbers so screen readers announce changes.
 */
export default function Scoreboard({
  state,
  statLabel,
  statMax,
}: {
  state: HotSeatState;
  statLabel: string;
  statMax?: number;
}) {
  return (
    <div
      className="grid grid-cols-2 gap-3"
      role="group"
      aria-label="Scoreboard"
    >
      {state.players.map((player) => {
        const stat = state.stats[player.id];
        const isActive = state.status === "playing" && state.currentTurn === player.id;
        const pct = statMax ? Math.max(0, Math.min(100, (stat / statMax) * 100)) : 0;

        return (
          <div
            key={player.id}
            className={`rounded-lg border p-3 transition-colors ${
              isActive
                ? "border-primary bg-primary/5"
                : "border-border bg-card"
            }`}
            aria-current={isActive ? "true" : undefined}
          >
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <span
                className={`text-sm font-semibold truncate ${
                  isActive ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {player.name}
              </span>
              {isActive && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                  Your turn
                </span>
              )}
            </div>

            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {statLabel}
              </span>
              <span
                className="text-xl font-bold font-mono text-foreground tabular-nums"
                aria-live="polite"
                aria-atomic="true"
              >
                {stat}
                {statMax ? (
                  <span className="text-xs text-muted-foreground font-normal">
                    {" "}
                    / {statMax}
                  </span>
                ) : null}
              </span>
            </div>

            {statMax !== undefined && (
              <div
                className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden"
                role="progressbar"
                aria-valuenow={stat}
                aria-valuemin={0}
                aria-valuemax={statMax}
                aria-label={`${player.name} ${statLabel}`}
              >
                <div
                  className="h-full bg-primary transition-[width] duration-300 motion-reduce:transition-none"
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}